/**
 * Detalle del reporte "Recordatorio semestral" (Fase 3 del plan de
 * morosidad, 2026-10-08).
 *
 * En junio y diciembre se le recuerda a toda casa con saldo que regularice
 * antes de que empiece la cuota siguiente. Es un envío masivo, así que la
 * pantalla cumple la convención #19 igual que el informe mensual:
 *
 * 1. **El correo real**, renderizado por el backend y pintado en un iframe
 *    (`srcDoc` + `sandbox=""`): lo que se aprueba es exactamente lo que sale.
 * 2. **La cobertura a la vista**: cuántas casas lo reciben, cuáles quedan
 *    fuera por no tener correo y cuáles tienen la marca "no avisar".
 * 3. **Prueba a tu correo** siempre disponible; **envío real** solo con el
 *    seguro abierto en Superadmin y en junio o diciembre, con confirmación y
 *    el número de destinatarios en el botón.
 *
 * Recibe datos y funciones por props para validarse en el preview dev-only.
 */
import { useState } from 'react';
import type {
  RecordatorioSemestralResponse,
  ResultadoEnvioRecordatorio,
  ResultadoPruebaRecordatorio,
} from '../../lib/api/reportes-tesoreria';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ModalConfirmar } from '../ui/ModalConfirmar';
import { CasasFuera, VistaPreviaCorreo } from './EnvioMasivo';

const MODO_LABEL = {
  cerrado: 'Cerrado: solo vista previa y prueba',
  manual: 'Manual: lo envías tú desde aquí',
  automatico: 'Automático: sale solo el día configurado',
} as const;

export interface RecordatorioSemestralProps {
  recordatorio: RecordatorioSemestralResponse;
  onEnviarPrueba: () => Promise<ResultadoPruebaRecordatorio>;
  onEnviar: () => Promise<ResultadoEnvioRecordatorio>;
  /** Se llama tras un envío real para que el contenedor recargue. */
  onEnviado?: () => void;
}

export function RecordatorioSemestral({
  recordatorio,
  onEnviarPrueba,
  onEnviar,
  onEnviado,
}: RecordatorioSemestralProps) {
  const [enviando, setEnviando] = useState<'prueba' | 'real' | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const r = recordatorio;
  const c = r.cobertura;
  const cuota = r.cuota_etiqueta;
  // Las avisadas hoy por morosidad no lo reciben hoy: el botón y la
  // confirmación dicen el número real.
  const pendientes = c.con_correo - c.ya_enviados - c.avisadas_hoy;
  const seguroAbierto = r.modo_envio !== 'cerrado';
  // El backend rechaza el envío real fuera de estas condiciones (409): la
  // pantalla no ofrece un botón que va a fallar, y dice por qué.
  const puedeEnviar = seguroAbierto && r.en_temporada && pendientes > 0;

  async function enviarPrueba() {
    setEnviando('prueba');
    setError(null);
    setAviso(null);
    try {
      const res = await onEnviarPrueba();
      setAviso(
        res.ok
          ? `Prueba enviada a ${res.enviado_a}. El envío a los vecinos sigue pendiente.`
          : `No se pudo enviar la prueba${res.motivo ? `: ${res.motivo}` : '.'}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar la prueba.');
    } finally {
      setEnviando(null);
    }
  }

  async function enviarReal() {
    setEnviando('real');
    setError(null);
    setAviso(null);
    try {
      const res = await onEnviar();
      const partes = [`Enviado a ${res.enviados} casa(s).`];
      if (res.omitidos > 0) {
        partes.push(
          `${res.omitidos} no lo recibieron ahora: ya lo tenían o ya recibieron un aviso de morosidad hoy.`,
        );
      }
      if (res.fallidos.length > 0) {
        partes.push(
          `Falló en ${res.fallidos.length}: casas ${res.fallidos.join(', ')}. ` +
            'Puedes volver a enviar: el reintento alcanza solo a esas.',
        );
      }
      setAviso(partes.join(' '));
      onEnviado?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar el recordatorio.');
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <div
          role="alert"
          className="bg-state-danger/10 border-[0.5px] border-state-danger/30 text-state-danger rounded-md p-3 text-sm"
        >
          {error}
        </div>
      )}
      {aviso && (
        <div className="bg-state-success/10 border-[0.5px] border-state-success/30 text-state-success rounded-md p-3 text-sm">
          {aviso}
        </div>
      )}

      {/* ── Estado del envío ── */}
      <Card title={`Recordatorio de la cuota del ${cuota}`}>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-text-secondary text-xs">Envío (Superadmin)</dt>
            <dd className="mt-0.5">
              {MODO_LABEL[r.modo_envio]}
              {r.modo_envio === 'automatico' &&
                ` (día ${r.dia_automatico} de junio y diciembre)`}
            </dd>
          </div>
          <div>
            <dt className="text-text-secondary text-xs">Temporada</dt>
            <dd className="mt-0.5">
              {r.en_temporada
                ? 'Junio / diciembre: se puede enviar.'
                : 'Se envía en junio y diciembre. Hoy solo vista previa y prueba.'}
            </dd>
          </div>
        </dl>
      </Card>

      {/* ── A quién le llega ── */}
      <Card title="A quién se envía">
        <p className="text-sm">
          <strong>{c.con_correo}</strong> de {c.casas_con_saldo} casas con saldo
          tienen responsable con correo.
          {c.ya_enviados > 0 && (
            <>
              {' '}
              Ya lo recibieron: <strong>{c.ya_enviados}</strong>.
            </>
          )}
          {c.avisadas_hoy > 0 && (
            <>
              {' '}
              Hoy no lo reciben <strong>{c.avisadas_hoy}</strong> que ya
              tuvieron aviso de morosidad hoy (no se repiten avisos).
            </>
          )}
        </p>
        <CasasFuera
          identificadores={c.identificadores_sin_correo}
          resumen={`${c.identificadores_sin_correo.length} casas no lo reciben (sin correo)`}
        />
        <CasasFuera
          identificadores={c.identificadores_sin_avisos}
          resumen={`${c.identificadores_sin_avisos.length} casas con la marca "no avisar" de Superadmin`}
          advertencia={false}
        />
      </Card>

      {/* ── El correo como lo ve el vecino ── */}
      <Card title="Vista del correo">
        {r.html ? (
          <VistaPreviaCorreo
            asunto={r.asunto ?? ''}
            html={r.html}
            nota="Ejemplo con la primera casa de la lista; cada vecino recibe su propia deuda."
          />
        ) : (
          <p className="text-sm text-text-secondary">
            No hay casas con saldo: no hay a quién enviar el recordatorio.
          </p>
        )}
      </Card>

      {/* ── Envío ── */}
      <Card title="Enviar">
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            variant="secondary"
            onClick={() => void enviarPrueba()}
            disabled={enviando !== null || !r.html}
          >
            {enviando === 'prueba' ? 'Enviando…' : 'Enviar prueba a mi correo'}
          </Button>
          <Button
            variant="primary"
            onClick={() => setConfirmando(true)}
            disabled={enviando !== null || !puedeEnviar}
          >
            {enviando === 'real' ? 'Enviando…' : `Enviar a ${pendientes} casa(s)`}
          </Button>
        </div>
        {!seguroAbierto && (
          <p className="text-sm text-state-warning mt-2">
            <strong>El envío real está cerrado.</strong> La prueba a tu correo
            sigue disponible. Para habilitarlo, cámbialo a "manual" o
            "automático" en Superadmin → Configuración de notificaciones.
          </p>
        )}
        <p className="text-xs text-text-secondary mt-2">
          La prueba va solo a tu correo y no marca ninguna casa como enviada.
          El envío real no se puede deshacer y cuenta como el aviso del mes de
          cada casa.
        </p>
      </Card>

      {confirmando && (
        <ModalConfirmar
          title="Enviar el recordatorio a los vecinos"
          mensaje={
            `Se enviará el recordatorio de la cuota del ${cuota} a ${pendientes} ` +
            'casa(s). Un correo enviado no se puede deshacer. Si todavía no lo ' +
            'revisaste, cancela y usa "Enviar prueba a mi correo".'
          }
          textoConfirmar="Enviar ahora"
          onConfirmar={async () => {
            setConfirmando(false);
            await enviarReal();
          }}
          onCancelar={() => setConfirmando(false)}
        />
      )}
    </div>
  );
}
